import gsap from "gsap";
import AbstractComponent from "./abstract-component";
import { MOTION, prefersReducedMotion } from "../motion/tokens";
import { addTick, ORDER } from "../motion/ticker";

/** Dévoilement : desktop seulement (footer plus court que l'écran), jamais en reduced-motion */
const REVEAL_MQ = "(min-width: 1190px) and (min-height: 700px)";

export default class SiteFooter extends AbstractComponent {
	constructor(el) {
		super(el);
		this.DOM = {};
		this.initDomElements();
		this.initEvents();
		this.initReveal();
	}

	/**
	 * Pied de page « dévoilé » : le footer reste dans le flux (Reveal / IO intacts),
	 * mais son contenu est contre-translaté pendant son entrée → il semble posé
	 * sous la page qui se retire (effet rideau), avec un voile qui se dissipe.
	 * Aucune lecture de layout par image (positions en cache), écriture transform only.
	 */
	initReveal() {
		this.DOM.content = this.$el.querySelector(":scope > .container");
		if (!this.DOM.content || prefersReducedMotion()) return;
		this._mq = window.matchMedia(REVEAL_MQ);
		this._cfgReveal = MOTION.footerReveal || {};

		this._onMq = () => (this._mq.matches ? this._enableReveal() : this._disableReveal());
		this._mq.addEventListener?.("change", this._onMq);
		this._onMq();
	}

	_enableReveal() {
		if (this._revealOn) return;
		this._revealOn = true;
		this.$el.classList.add("is-reveal");
		this._setY = gsap.quickSetter(this.DOM.content, "y", "px");
		this._last = null;
		this._frame = 0;
		this._measureReveal();

		this._unTick = addTick(
			() => {
				this._frame += 1;
				if (this._frame % 120 === 0) this._measureReveal();
				this._renderReveal();
			},
			{ order: ORDER.scene, label: "footer-reveal" }
		);
		this._onRevealResize = () => this._measureReveal();
		window.addEventListener("resize", this._onRevealResize, { passive: true });
		window.addEventListener("page-transition:done", this._onRevealResize);
	}

	_disableReveal() {
		if (!this._revealOn) return;
		this._revealOn = false;
		this._unTick?.();
		this._unTick = null;
		window.removeEventListener("resize", this._onRevealResize);
		window.removeEventListener("page-transition:done", this._onRevealResize);
		this.$el.classList.remove("is-reveal");
		this.$el.style.removeProperty("--footer-veil");
		gsap.set(this.DOM.content, { clearProps: "transform" });
	}

	_scrollY() {
		/* Scroll natif (Lenis l'applique avant nous dans l'horloge) : toujours juste,
		 * même quand Lenis est stoppé pendant une transition (sa valeur reste figée) */
		return window.scrollY || 0;
	}

	_measureReveal() {
		const r = this.$el.getBoundingClientRect();
		this._top = r.top + this._scrollY();
		this._height = this.$el.offsetHeight || r.height;
		this._vh = window.innerHeight || 1;
	}

	_renderReveal() {
		const visible = this._scrollY() + this._vh - this._top;
		/* Hors écran : position de départ posée une fois, puis plus rien */
		const p = Math.max(0, Math.min(1, visible / Math.min(this._height, this._vh)));
		if (p === this._last) return;
		this._last = p;
		const inv = 1 - p;
		const shift = this._cfgReveal.contentY ?? -64;
		/* Contenu : suit ~60 % du défilement à l'entrée (rideau) + léger décalage résorbé */
		this._setY(Math.round((-inv * this._height * 0.6 + inv * shift) * 100) / 100);
		this.$el.style.setProperty("--footer-veil", (inv * (this._cfgReveal.veil ?? 0.5)).toFixed(3));
	}

	initDomElements() {
		this.DOM.scrollTopBtn = this.$el.querySelector(
			".js-scroll-to-top-button, .c-site-footer__top"
		);
	}

	initEvents() {
		const btn = this.DOM.scrollTopBtn;
		if (!btn) return;

		btn.addEventListener(
			"click",
			(e) => {
				e.preventDefault();
				e.stopPropagation();
				if (this.app?.$scroll?.lenis) {
					this.app.$scroll.lenis.scrollTo("top", {
						duration: MOTION.lenis?.scrollToDuration ?? 0.9,
						easing: (t) => 1 - Math.pow(1 - t, 3),
					});
				} else if (this.app?.$scroll?.scrollTo) {
					this.app.$scroll.scrollTo(0);
				} else {
					try {
						window.scrollTo({ top: 0, behavior: "smooth" });
					} catch (err) {
						window.scrollTo(0, 0);
					}
				}
			},
			{ passive: false }
		);
	}

	destroy() {
		this._disableReveal();
		this._mq?.removeEventListener?.("change", this._onMq);
	}
}
